/**
 * Mobile Tabs Store
 *
 * Manages the active tab state for mobile navigation.
 * Provides functionality to switch tabs and scroll to top.
 */

import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';

const ACTIONS = {
  SET_ACTIVE_TAB: 'SET_ACTIVE_TAB',
  SWITCH_TO_BETS: 'SWITCH_TO_BETS',
} as const;

type TabId = 'bets' | 'wallet' | 'settings';

interface MobileTabsState {
  activeTab: TabId;
  tabIndex: number;
}

type SetActiveTabAction = { type: typeof ACTIONS.SET_ACTIVE_TAB; payload: TabId };
type SwitchToBetsAction = { type: typeof ACTIONS.SWITCH_TO_BETS };
type MobileTabsAction = SetActiveTabAction | SwitchToBetsAction;

const initialState: MobileTabsState = {
  activeTab: 'bets',
  tabIndex: 0,
};

const TABS: TabId[] = ['bets', 'wallet', 'settings'];

function mobileTabsReducer(state: MobileTabsState, action: MobileTabsAction): MobileTabsState {
  switch (action.type) {
    case ACTIONS.SET_ACTIVE_TAB: {
      const tabIndex = TABS.indexOf(action.payload);
      return {
        ...state,
        activeTab: action.payload,
        tabIndex: tabIndex >= 0 ? tabIndex : 0,
      };
    }
    case ACTIONS.SWITCH_TO_BETS:
      return {
        ...state,
        activeTab: 'bets',
        tabIndex: 0,
      };
    default:
      return state;
  }
}

export interface MobileTabsContextValue extends MobileTabsState {
  setActiveTab: (tab: TabId) => void;
  switchToBets: () => void;
}

const MobileTabsContext = createContext<MobileTabsContextValue | null>(null);

interface MobileTabsProviderProps {
  children: ReactNode;
}

export function MobileTabsProvider({ children }: MobileTabsProviderProps): React.ReactElement {
  const [state, dispatch] = useReducer(mobileTabsReducer, initialState);

  const setActiveTab = useCallback((tab: TabId) => {
    dispatch({ type: ACTIONS.SET_ACTIVE_TAB, payload: tab });
  }, []);

  const switchToBets = useCallback(() => {
    dispatch({ type: ACTIONS.SWITCH_TO_BETS });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const value: MobileTabsContextValue = {
    ...state,
    setActiveTab,
    switchToBets,
  };

  return (
    <MobileTabsContext.Provider value={value}>
      {children}
    </MobileTabsContext.Provider>
  );
}

export function useMobileTabsContext(): MobileTabsContextValue {
  const context = useContext(MobileTabsContext);
  if (!context) {
    throw new Error('useMobileTabsContext must be used within a MobileTabsProvider');
  }
  return context;
}

export default MobileTabsContext;
